import { resourceKeys } from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { makeLookup } from '../lib/lookup.js';
import { queryClient } from '../lib/query-client.js';
import { queryKeys } from '../lib/queries.js';

/**
 * The pickers a mandate form needs: the asset it covers and the colleague
 * responsible for it.
 */
export const { search: searchProperties, resolve: resolveProperty } = makeLookup({
  key: resourceKeys.all('properties'),
  search: async (query, signal) =>
    assertOk(
      await api.properties.list({
        query: { page: 1, pageSize: 20, order: 'asc', ...(query ? { q: query } : {}) },
        fetchOptions: { signal },
      }),
    ).body.items,
  get: async (id, signal) =>
    assertOk(await api.properties.get({ params: { id }, fetchOptions: { signal } })).body,
  toOption: (item) => ({ value: item.id, label: item.displayLabel }),
});

/**
 * There is no `GET /users/:id`: the lookup is bounded and the id is already in
 * the list it came from, so a restored filter resolves by searching rather than
 * by fetching one user. The bounded list is itself cached, so every filter bar
 * that shows a colleague shares the one request.
 */
const lookupUsers = (query: string, limit: number, signal: AbortSignal) =>
  api.users.lookup({ query: { limit, ...(query ? { q: query } : {}) }, fetchOptions: { signal } });

export const { search: searchUsers, resolve: resolveUser } = makeLookup({
  key: queryKeys.agencyUsers,
  search: async (query, signal) => assertOk(await lookupUsers(query, 20, signal)).body,
  get: async (id) => {
    const everyone = await queryClient.fetchQuery({
      queryKey: [...queryKeys.agencyUsers, 'lookup', 'all'],
      queryFn: async ({ signal }) => assertOk(await lookupUsers('', 100, signal)).body,
      staleTime: 60_000,
    });
    const found = everyone.find((item) => item.id === id);
    if (!found) throw new Error(`User ${id} is not in the lookup`);
    return found;
  },
  toOption: (item) => ({ value: item.id, label: item.name }),
});
